
import { Component, Ref, Vue, Watch, Prop } from 'vue-property-decorator'
import markdownit from 'markdown-it'
import { ITranslationService } from '@/services/translation/ITranslationService'
import { IUserService } from '@/services/user/IUserService'
import { RawLocation } from 'vue-router/types/router'

@Component
export default class LicenseDialog extends Vue {
  @Prop({ type: [Object, String] }) readonly to?: RawLocation

  private translationService!: ITranslationService

  markdownHtml = ''
  licenseLoaded = false
  licenseRead = false

  currentLanguage = ''
  languages: Map<string, string> = new Map()
  md = markdownit()
  @Ref('scrollContainer') scrollContainerRef!: HTMLElement

  created(): void {
    this.translationService = this.$services.get<ITranslationService>('translation')
    this.currentLanguage = this.translationService.getCurrentLanguageCode()
    const defaultRender =
      this.md.renderer.rules.heading_open ||
      function (tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options)
      }
    this.md.renderer.rules.heading_open = function (tokens, idx, options, env, self) {
      const token = tokens[idx]
      if (token.tag === 'h1') {
        return '<h1 class="title is-2">'
      }
      if (token.tag === 'h2') {
        return '<h2 class="subtitle mt-5 mb-1 is-4">'
      }
      return defaultRender(tokens, idx, options, env, self)
    }
  }

  async mounted(): Promise<void> {
    this.languages = await this.translationService.getAvailableLanguages()
  }

  async loadMarkdown(language: string): Promise<void> {
    this.licenseLoaded = false
    const response = await fetch(`/licenses/license-${language}.md`)
    const markdown = await response.text()
    if (response.status !== 200 || markdown.startsWith('<!DOCTYPE html')) {
      console.error('License file not found or fallback returned HTML')
      throw new Error('License file not found')
    }
    this.markdownHtml = this.md.render(markdown)
    this.licenseLoaded = true
  }

  handleLicenseScroll(): void {
    //make sure it has a scrollbar
    if (this.scrollContainerRef.scrollHeight === this.scrollContainerRef.clientHeight || this.licenseLoaded === false)
      return
    const atBottom =
      this.scrollContainerRef.scrollTop + this.scrollContainerRef.clientHeight >=
      this.scrollContainerRef.scrollHeight - 50

    if (atBottom) {
      this.licenseRead = true
    }
  }

  @Watch('currentLanguage', { immediate: false })
  async changeLanguage(): Promise<void> {
    await this.translationService.useLanguage(this.currentLanguage)
    this.licenseRead = false
    try {
      await this.loadMarkdown(this.currentLanguage)
      if (this.scrollContainerRef.scrollHeight === this.scrollContainerRef.clientHeight) {
        this.licenseRead = true
      }
    } catch (error) {
      console.error('Error loading markdown:', error)
      this.markdownHtml = `<p class="has-text-danger">${this.$t('components.license.dialog.accept')}</p>`
      this.licenseLoaded = true
      this.licenseRead = false
    }
  }

  async licenseUser(): Promise<void> {
    const userService = this.$services.get<IUserService>('user')
    await userService.updateCurrentUser({ acceptedLicenseAgreement: true })
    this.$router.push(this.to ? this.to : { name: 'Home' })
  }
}
